import React, { FC, useCallback } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Person from '@mui/icons-material/Person'
import CoreText from 'components/Core/CoreText'
import CoreButton from 'components/Core/CoreButton'
import VSVModal from './components/VSVModal'
import InfoList, { InfoListItem } from './components/InfoList'
import { Block } from './Availabilities'
import { Calendar } from './types'
import EventAvailableIcon from 'svgs/event-available'
import DateIcon from 'svgs/calendar'
import TimeIcon from 'svgs/clock'
import Spinner from 'svgs/spinner'

dayjs.extend(utc)
dayjs.extend(timezone)

interface ConfirmBookingModalProps {
  calendar: Calendar
  time: Block
  submitting: boolean
  onClose?: () => void
  onConfirm?: () => void
}

const ConfirmBookingModal: FC<ConfirmBookingModalProps> = ({ calendar, time, submitting, onClose, onConfirm }) => {
  const renderButtons = useCallback(
    () => (
      <>
        <CoreButton
          text="Confirm"
          kind="primary"
          disabled={submitting}
          icon={submitting ? <Spinner strokeWidth={16} /> : undefined}
          onClick={onConfirm}
        />
        <CoreButton text="Close" kind="secondary" disabled={submitting} onClick={onClose} />
      </>
    ),
    [submitting, onConfirm, onClose]
  )

  const startTime = dayjs(time.start).tz(calendar.property.time_zone)
  const endTime = dayjs(time.end).tz(calendar.property.time_zone)

  return (
    <VSVModal
      title="Confirm site visit"
      onClose={onClose}
      buttons={renderButtons}
      icon={<EventAvailableIcon />}
      kind="success"
    >
      <p className="tw:text-secondary-black">
        {'Reschedulings may result in the project being reassigned to another vendor'}
      </p>
      <InfoList>
        <div className="tw:flex tw:gap-4 tw:items-center tw:text-sm tw:font-semibold tw:py-2 tw:mt-4">
          <div className="tw:size-8 tw:bg-neutrals-200 tw:shrink-0 tw:rounded-full tw:flex tw:items-center tw:justify-center">
            <Person className="tw:!size-5" />
          </div>
          <div className="tw:grow">{calendar.property.full_name}</div>
        </div>
        <InfoListItem icon={<DateIcon />}>
          <CoreText.Paragraph size="m" color="primaryBlack">
            {startTime.format('ddd, MMM D')}
          </CoreText.Paragraph>
        </InfoListItem>
        <InfoListItem icon={<TimeIcon />}>
          <CoreText.Paragraph size="m" color="primaryBlack">
            {`${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`}
          </CoreText.Paragraph>
          <CoreText.Paragraph size="m" weight="light" color="primaryBlack">
            {startTime.format('zzz')}
          </CoreText.Paragraph>
        </InfoListItem>
      </InfoList>
    </VSVModal>
  )
}

export default ConfirmBookingModal
